import IconExpandMore from 'assets/icons/expand-more.svg';
import cn from 'classnames';
import {
  AllergySegmentationIds,
  NutritionSegmentationIds,
} from 'constants/segmentation';
import { useNeedCapture } from 'modules/landingpage/components/need-capture/state';
import { DraftState } from 'modules/landingpage/components/need-capture/state/interface';
import { AnimatePresence, m } from 'motion/react';
import { FormattedMessage } from 'react-intl';

interface Props {
  isOpen: boolean;
  selectedOption: DraftState;
  onSelectAllergies: (value: AllergySegmentationIds) => void;
  onSelectNeeds: (value: NutritionSegmentationIds) => void;
}

/**
 * It renders a list of age. It's used in the need capture component.
 */
const NeedsSelectorDesktop: React.FC<Props> = ({
  isOpen,
  selectedOption,
  onSelectAllergies,
  onSelectNeeds,
}) => {
  const { filteredNeedsEntries } = useNeedCapture();

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
          className="bg-other-white text-grey-800 absolute z-50 mt-1 flex max-h-72 w-full flex-col overflow-hidden overflow-y-auto rounded-lg py-1 text-sm font-normal shadow-sm"
        >
          {filteredNeedsEntries.map(([key, value]) => (
            <>
              <button
                key={key}
                className={cn(
                  'flex items-center justify-between px-4 py-2 text-left',
                  selectedOption?.draftNeeds.includes(value)
                    ? 'bg-grey-100 hover:bg-grey-100'
                    : 'hover:bg-grey-50'
                )}
                onClick={() => onSelectNeeds(value)}
              >
                <div className="leading-4">
                  <FormattedMessage
                    id={`common:pet-profile:needs-selector:${value}`}
                  />

                  <span className="text-grey-700 block">
                    <FormattedMessage
                      id={`common:pet-profile:needs-selector:${value}-description`}
                    />
                  </span>
                </div>
                {value === NutritionSegmentationIds.Intolerance && (
                  <IconExpandMore
                    className={cn(
                      'h-6 w-6 transition-transform',
                      selectedOption?.draftNeeds.includes(value) &&
                        'rotate-180 transform'
                    )}
                  />
                )}
              </button>
              <AnimatePresence>
                {value === NutritionSegmentationIds.Intolerance &&
                  selectedOption.draftNeeds.includes(value) && (
                    <m.div
                      className="relative"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                      <div className="flex flex-col">
                        {Object.entries(AllergySegmentationIds).map(
                          ([key, value]) => (
                            <button
                              key={key}
                              className={cn(
                                'py-1.5 pl-8 text-left',
                                selectedOption.draftAllergies.includes(value)
                                  ? 'bg-grey-100 hover:bg-grey-100'
                                  : 'hover:bg-grey-50'
                              )}
                              onClick={() => onSelectAllergies(value)}
                            >
                              <FormattedMessage
                                id={`common:allergy:${value}`}
                              />
                            </button>
                          )
                        )}
                      </div>
                    </m.div>
                  )}
              </AnimatePresence>
            </>
          ))}
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default NeedsSelectorDesktop;
